import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DisplayMode } from '@api-consent/types/common.js';
import type { TefComs4csMicroFrontend } from '@src/components/tef-coms4cs.js';
import { localDate, localTime, replaceElementsBound } from '@src/components/templates/util.js';
import { comsStore } from '@src/state/ComsStore.js';
import { ConsentGroupStore } from '@src/state/ConsentGroupStore.js';
import type { ConsentStore } from '@src/state/ConsentStore.js';

export function consentTemplate(this: TefComs4csMicroFrontend, consent: ConsentStore, group: ConsentGroupStore):
TemplateResult {
  const featureTemplate = this.getTemplateFn('featureTemplate');
  const iconType = consent.granted ? 'check' : 'cancel';

  function getPosition() {
    if (consent.client && consent.date) return 'start';
    return consent.features.length ? 'start' : 'center';
  }

  const textColor = consent.deprecated ? 'disabled' : undefined;

  let backgroundColor = 'none'; // transparent
  let background = ''; // no border

  if (consent.deprecated) {
    backgroundColor = '#eee';
  }
  if (consent.modifiedFromInitialSelfOrOneOfTheFeatures) {
    backgroundColor = ''; // default white (makes background="legal-1" work)
    background = 'legal-1';
  }
  if (group.displayMode === DisplayMode.TEXT || group.displayMode === DisplayMode.LIST) {
    background = '';
  }

  const lastChangeAtHtml = html`
    <one-list-item size="xs">
      <one-icon name="clock" variant="outline" slot="icon"></one-icon>
      <one-link size="xs"
        role="button"
        data-testid="consent-history-link-${consent.id}"
        @click=${() => { /* eslint-disable @typescript-eslint/indent */
          comsStore.history!.configId = consent.configId;
          comsStore.history!.consentShortScope = consent.shortScope;
        } /* eslint-enable @typescript-eslint/indent */}
      >
        Letzte Änderung am
        ${localDate(consent.date)} um ${localTime(consent.date)} Uhr über ${consent.clientDisplay ?? consent.client}
      </one-link>&nbsp;
    </one-list-item>
  `;

  const consentContent = html`
    <one-stack gap="sm">
      <one-text as="p" color="${ifDefined(textColor)}">
        ${unsafeHTML(replaceElementsBound(this, consent.scope, consent.deprecated))}
        ${comsStore.config?.showVersions ? html`<small>(Vers. ${consent.version})</small>` : nothing}
      </one-text>

      ${consent.features.length ? html`
        <one-stack gap="xs">
          ${consent.features.map((f) => html`${featureTemplate(f, !!consent.deprecated)}`)}
        </one-stack>
      ` : nothing}
      ${consent.client && consent.date ? lastChangeAtHtml : nothing}
    </one-stack>
  `;

  const iconHtml = html`
    <one-list icon="${iconType}" size="lg">
      <one-list-item>
        <one-icon name="${iconType}" size="lg" variant="outline" slot="icon"></one-icon>
        ${consentContent}
      </one-list-item>
    </one-list>`;

  const checkboxHtml = html`
    <tef-coms-tri-state-button
      data-testid="${consent.id}"
      .consent=${consent}
      .checked=${consent.granted}>
    </tef-coms-tri-state-button>
    ${consentContent}
  `;

  let consentHtml;
  if (consent.deprecated) {
    consentHtml = iconHtml;
  } else if (group.displayMode === DisplayMode.TEXT) {
    consentHtml = html`<div>${consentContent}</div>`;
  } else if (group.displayMode === DisplayMode.LIST) {
    consentHtml = html`<one-list icon="bullet-ul-list" icon-variant="solid"><one-list-item>${consentContent}</one-list-item></one-list>`;
  } else {
    consentHtml = checkboxHtml;
  }

  return html`
    ${consent.hintBefore ? html`
      <one-text as="div">${unsafeHTML(consent.hintBefore)}</one-text>` : nothing}
      <one-paper padding="sm" text-color="make-links-blau" background-color="${backgroundColor}" background="${background}" data-testid="consent-wrapper-${consent.id}">
        ${consent.modeconstraint === 'edit' ? html`<small>Diese Einwilligung ist aus der Zusammenführung der vorhergehenden Einwilligungen entstanden</small>` : nothing}
        <one-cluster gap="xs" no-wrap y="${getPosition()}" >
          ${consentHtml}
        </one-cluster>
      </one-paper>
      ${consent.hintAfter ? html`<one-text as="div">${unsafeHTML(consent.hintAfter)}</one-text>` : nothing}
    `;
}
